import cn from 'classnames'
import * as React from 'react'
import Select, {
  MultiValue,
  OptionProps,
  StylesConfig,
  components,
} from 'react-select'

import { FieldWrapper } from 'components/FieldWrapper'
import { FieldWrapperProps } from 'components/FieldWrapper/FieldWrapper'

import { ReactComponent as CheckIcon } from 'assets/images/svg/check.svg'

import styles from './SelectMulti.module.scss'

export interface SelectMultiOption {
  label: string
  value: string
}

interface Props {
  options: SelectMultiOption[]
  value?: SelectMultiOption[]
  placeholder?: string
  containerClassName?: string
  isDisabled?: boolean
  onChange?(options: SelectMultiOption[]): void
}

const CheckboxOption: React.FC<
  OptionProps<SelectMultiOption, true>
> = props => {
  const { children, isSelected } = props
  return (
    <components.Option {...props}>
      <div className={styles.option__content}>
        {children}
        <span
          className={cn(
            styles.option__check,
            isSelected ? styles['option__check--active'] : null,
          )}
        >
          <CheckIcon width={14} height={11} />
        </span>
      </div>
    </components.Option>
  )
}

export const SelectMulti: React.FC<Props & FieldWrapperProps> = ({
  label,
  required,
  error,
  withError,
  containerClassName,
  options,
  value,
  placeholder = 'Выберите',
  isDisabled,
  onChange,
}) => {
  const handleChange = (selected: MultiValue<SelectMultiOption>) => {
    onChange?.(Array.from(selected))
  }

  const colourStyles: StylesConfig<SelectMultiOption, true> = {
    control: base => ({
      ...base,
      minHeight: 40,
      borderRadius: 5,
      border: '1px solid #d6d6d6',
      boxShadow: 'none',
      '&:hover': { borderColor: '#2b5da6' },
      backgroundColor: '#fff',
      cursor: 'pointer',
    }),
    valueContainer: base => ({
      ...base,
      maxHeight: 88,
      flexWrap: 'nowrap',
      overflow: 'auto',

      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    }),
    multiValue: base => ({
      ...base,
      backgroundColor: '#eef2fa',
      borderRadius: 4,
      minWidth: 'auto',
    }),
    multiValueLabel: base => ({
      ...base,
      color: '#333333',
      fontSize: 14,
    }),
    multiValueRemove: base => ({
      ...base,
      color: '#2b5da6',
      ':hover': {
        backgroundColor: '#2b5da6',
        color: '#fff',
        cursor: 'pointer',
      },
    }),
    placeholder: base => ({
      ...base,
      fontStyle: 'normal',
      color: '#999999',
      fontSize: 14,
    }),
    indicatorsContainer: base => ({
      ...base,
      color: '#999999',
    }),
    menu: base => ({
      ...base,
      zIndex: 10,
    }),
    option: (base, props) => ({
      ...base,
      backgroundColor: props.isSelected ? '#eef2fa' : '#fff',
      ':active': {
        backgroundColor: props.isSelected ? '#eef2fa' : '#f0f0f0',
      },
    }),
  }

  return (
    <FieldWrapper
      containerClassName={cn(styles.container, containerClassName)}
      label={label}
      required={required}
      error={error}
      withError={withError}
    >
      <Select
        className={styles.select}
        classNamePrefix="select-multi"
        options={options}
        value={value}
        isMulti
        isSearchable
        isDisabled={isDisabled}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        placeholder={placeholder}
        styles={colourStyles}
        components={{ Option: CheckboxOption }}
        onChange={handleChange}
      />
    </FieldWrapper>
  )
}
